import { ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react'

import { t } from 'lib/i18n'

import { Icon } from './Icon'

type Tone = 'success' | 'error' | 'info'

interface Toast {
  id: number
  tone: Tone
  message: string
  details?: string[]
}

type Notify = (message: string, tone?: Tone, details?: string[]) => void

const ToastContext = createContext<Notify>(() => undefined)

let nextId = 1

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<Toast[]>([])

  const dismiss = useCallback((id: number) => setToasts((current) => current.filter((toast) => toast.id !== id)), [])

  const notify = useCallback<Notify>(
    (message, tone = 'success', details) => {
      const id = nextId++
      setToasts((current) => [...current, { details, id, message, tone }])
      setTimeout(() => dismiss(id), tone === 'error' ? 8000 : 3500)
    },
    [dismiss]
  )

  const value = useMemo(() => notify, [notify])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast toast--${toast.tone}`}>
            <Icon name={toast.tone === 'error' ? 'alert' : toast.tone === 'info' ? 'info' : 'check'} />
            <div className="toast__body">
              <strong>{toast.message}</strong>
              {toast.details?.length ? (
                <ul>
                  {toast.details.map((detail) => (
                    <li key={detail}>{detail}</li>
                  ))}
                </ul>
              ) : null}
            </div>
            <button className="icon-button" onClick={() => dismiss(toast.id)} aria-label={t('إغلاق', 'Close')}>
              <Icon name="close" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export const useToast = () => useContext(ToastContext)
